import { Card, CardContent } from "@commerce-os/ui";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { CheckCircle2Icon } from "lucide-react";

import { useT } from "../app/i18n";
import { Brand } from "../features/shell/brand";
import { LanguageMenu } from "../features/shell/language-menu";

/** Signed-out pages: the form in a card, with a brand panel beside it on wide screens. A signed-in user goes home. */
export const Route = createFileRoute("/_auth")({
  beforeLoad: ({ context }) => {
    if (context.session.token() !== undefined) throw redirect({ to: "/" });
  },
  component: AuthLayout,
});

const POINTS = ["offline", "stock", "channels"] as const;

function AuthLayout() {
  const { t } = useT();
  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      <aside className="relative hidden overflow-hidden bg-primary p-12 text-primary-foreground lg:flex lg:flex-col lg:justify-between">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 size-[28rem] rounded-full bg-white/10 blur-3xl"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-32 -left-16 size-[24rem] rounded-full bg-black/10 blur-3xl"
        />
        <div className="relative flex items-center">
          <img
            src="/brand/logo-white.svg"
            alt={t("appName")}
            width={421}
            height={142}
            className="h-10 w-auto"
          />
        </div>
        <div className="relative flex max-w-md flex-col gap-8">
          <h2 className="text-4xl leading-tight font-semibold tracking-tight text-balance">
            {t("auth:brand.tagline")}
          </h2>
          <ul className="flex flex-col gap-3 text-base/7 text-primary-foreground/90">
            {POINTS.map((point) => (
              <li key={point} className="flex items-start gap-3">
                <CheckCircle2Icon className="mt-1 size-5 shrink-0 opacity-90" aria-hidden />
                <span>{t(`auth:brand.points.${point}`)}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="relative text-sm text-primary-foreground/70">{t("auth:brand.footer")}</p>
      </aside>
      <div className="flex flex-col">
        <header className="flex h-16 items-center justify-between px-4 sm:px-6">
          <Brand className="lg:invisible" />
          <LanguageMenu />
        </header>
        <main className="flex flex-1 items-start justify-center px-4 pb-16 sm:items-center">
          <Card className="w-full max-w-sm">
            <CardContent className="p-6 sm:p-8">
              <Outlet />
            </CardContent>
          </Card>
        </main>
      </div>
    </div>
  );
}
